import { t } from 'i18next';
import {
  applyReduction,
  contextContent,
  getKindIdentifierMatchFromContextMap,
  leavesAfterFilter,
  referenceKeyToName,
} from '@/vantage/components/evidence_table/helpers/utilities';
import type { ContextSchema } from '@/vantage/types/Context.types';
import type { ExtensionColumns } from '@/vantage/types/TableConfig.types';
import type { IAggFuncParams, RowNode } from 'ag-grid-community';
import _ from 'lodash';
import { VantageNames } from '@/vantage/constants';
import { optimizedExtreme } from '@/vantage/utilities';
import { itemIdField } from '@/vantage/context';

const toNumericValues = (values: any[]): number[] =>
  values
    .filter((value) => !_.isNil(value) && value !== '')
    .map(_.toNumber)
    .filter(_.isFinite);

const reduceAggregator = <ValueType, ResultType>(
  params: IAggFuncParams,
  reducer: (values: ValueType[]) => ResultType,
): ResultType | number => {
  const field = params.colDef.field?.toLowerCase();

  if (field?.includes('groupitems')) {
    const rowNode = params.rowNode as RowNode;
    return rowNode?.childrenAfterAggFilter?.length || rowNode?.childrenAfterFilter?.length || 0;
  }

  if (field?.includes('descendant-items')) {
    return leavesAfterFilter(params.rowNode as RowNode)?.length || 0;
  }

  const rowNode = params.rowNode as RowNode;
  if (rowNode?.group !== undefined && params.colDef.field) {
    const leafNodesValues = leavesAfterFilter(rowNode).map((node) => node.data[params.colDef.field!]);
    return reducer(leafNodesValues);
  }

  return reducer(params.values);
};

const sumAggregator = (params: IAggFuncParams) => reduceAggregator(params, (values) => _.sum(toNumericValues(values)));

const avgAggregator = (params: IAggFuncParams) => {
  const result = reduceAggregator(params, (values) => values);
  if (typeof result === 'number') {
    return result;
  }
  const numericValues = toNumericValues(result);
  return numericValues.length > 0 ? _.mean(numericValues) : null;
};

const countAggregator = (params: IAggFuncParams) =>
  reduceAggregator(params, (values) => values.filter((value) => !_.isNil(value) && value !== '').length);

const defaultAggregator = (
  extensionColumns: ExtensionColumns,
  nonEvidenceContextMap: React.MutableRefObject<Map<string, ContextSchema[]>>,
) => {
  return (params: IAggFuncParams) => {
    if (params.colDef.field === VantageNames.Columns.Suppressed && params.rowNode.field) {
      const conditionId = params.rowNode.key?.toLowerCase() ?? '';
      const kindIdentifierMatches = getKindIdentifierMatchFromContextMap(
        nonEvidenceContextMap.current,
        referenceKeyToName(params.rowNode.field),
        VantageNames.Extensions.Suppression,
      );
      return kindIdentifierMatches.some((context) => context.kindId === conditionId.toUpperCase());
    } else {
      const columnParams = extensionColumns[referenceKeyToName(params.colDef.field!)];
      const valueSet = new Set(params.values);
      if (valueSet.size > 1) {
        return t('VANTAGE.EVIDENCE_TABLE.MULTIPLE_VALUES');
      } else if (valueSet.size === 1) {
        return params.values[0];
      } else if (!_.isUndefined(columnParams?.meta.aggFuncDefaultValue)) {
        return columnParams.meta.aggFuncDefaultValue;
      } else if (columnParams?.meta.colDefType === 'boolean') {
        return false;
      } else {
        return t('VANTAGE.EVIDENCE_TABLE.UNABLE_TO_AGGREGATE');
      }
    }
  };
};

const generateLookupAggregator = (
  nonEvidenceContextMap: React.MutableRefObject<Map<string, ContextSchema[]>>,
  extensionColumns: ExtensionColumns,
  contextItemIdMapping?: Record<string, string>,
) => {
  return (params: IAggFuncParams) => {
    const columnName = params?.column?.getColId();
    if (params?.rowNode?.field) {
      const columnContextKind = referenceKeyToName(params.rowNode.field);
      const contextIdentifier = extensionColumns[columnName].meta.kindIdentifierPairs?.[columnContextKind] ?? '';
      const groupValue = params?.rowNode?.aggData?.[itemIdField(params?.rowNode?.field, contextItemIdMapping)];
      const relevant = (context: ContextSchema) => context.purview?.[columnContextKind] === groupValue;
      if (nonEvidenceContextMap) {
        const kindIdentifierMatches = getKindIdentifierMatchFromContextMap(
          nonEvidenceContextMap.current,
          columnContextKind,
          contextIdentifier,
        );
        if (extensionColumns[columnName].meta.colDefType === 'boolean') {
          return kindIdentifierMatches.some(relevant);
        } else {
          const contextArrayOfInterest: ContextSchema[] = kindIdentifierMatches.filter(relevant);
          if (contextArrayOfInterest.length > 0) {
            return applyReduction(
              contextArrayOfInterest,
              extensionColumns[columnName].meta.kindReducerPairs?.[columnContextKind],
              contextContent(extensionColumns[columnName].meta.contextPath!),
              extensionColumns[columnName].meta.category,
            );
          }
        }
      }
      return extensionColumns[columnName]?.meta?.colDefDefaultValue || null;
    }
  };
};

export const dateExtremesAggregator = (params: IAggFuncParams, extreme: 'min' | 'max'): Date | undefined => {
  const field = params.colDef.field;
  if (!field) return;

  const dates = leavesAfterFilter(params.rowNode as RowNode)
    .map(({ data }) => data?.[field])
    .filter((v): v is string | number | Date => v != null && v !== '')
    .map((v) => new Date(v))
    .filter((d) => !Number.isNaN(d.valueOf()));

  return dates.length ? new Date(optimizedExtreme(dates, extreme, (d) => d.valueOf())) : undefined;
};

export const aggFuncsGenerator = (
  extensionColumns: ExtensionColumns,
  nonEvidenceContextMap: React.MutableRefObject<Map<string, ContextSchema[]>>,
  contextItemIdMapping?: Record<string, string>,
) => {
  return {
    first: (params: IAggFuncParams) => dateExtremesAggregator(params, 'min'),
    last: (params: IAggFuncParams) => dateExtremesAggregator(params, 'max'),
    sum: sumAggregator,
    avg: avgAggregator,
    count: countAggregator,
    Aggregated: defaultAggregator(extensionColumns, nonEvidenceContextMap),
    LookupAggregator: generateLookupAggregator(nonEvidenceContextMap, extensionColumns, contextItemIdMapping),
  };
};
